/**
 * Create Meal Plan Form Component
 *
 * Form component for creating new meal plans with dietary preferences, restrictions,
 * and budget constraints. Adapted from the reference design to use the BiteWise
 * healthcare provider platform design system.
 *
 * @module components/meal-plans/CreateMealPlanForm
 */

import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router';
import { useTheme } from '@/hooks/useTheme';
import type { CreateMealPlanRequestFrontend, MealType } from '@/types/mealPlan';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { FormField } from '@/components/ui/FormField';
import { MultiSelectInput } from '@/components/ui/MultiSelectInput';
import { NumberField } from '@/components/ui/NumberField';
import { Input } from '@/components/ui/Input';
import { TextArea } from '@/components/ui/TextArea';
import { ButtonGroup, type ButtonGroupOption } from '@/components/ui/ButtonGroup';
import { cn } from '@/utils/cn';
import { calculateEstimatedTimeSeconds, formatEstimatedTime } from '@/utils/mealPlanEstimation';
import { getLoggerWithPrefix } from '@/services/shared/LoggerFactory';

/**
 * Props interface for CreateMealPlanForm component
 */
export interface CreateMealPlanFormProps {
  /** Function to handle form submission */
  onSubmit?: (formData: CreateMealPlanRequestFrontend) => Promise<void>;
  /** Loading state indicator */
  isSubmitting?: boolean;
  /** Error message if request failed */
  error?: string | null;
  /** Function to clear error state */
  onClearError?: () => void;
}

const mealTypeOptions: { value: MealType; label: string }[] = [
  { value: 'breakfast', label: 'Breakfast' },
  { value: 'morning_tea', label: 'Morning Tea' },
  { value: 'lunch', label: 'Lunch' },
  { value: 'afternoon_tea', label: 'Afternoon Tea' },
  { value: 'dinner', label: 'Dinner' },
  { value: 'dessert', label: 'Dessert' },
];

const dietaryRestrictionOptions = [
  'DASH (Dietary Approaches to Stop Hypertension)',
  'Flexitarian',
  'High-carb / Low-fat',
  'Ketogenic (Keto)',
  'Lacto vegetarian',
  'Lacto-ovo vegetarian',
  'Low-carb / High-protein',
  'MIND Diet (Mediterranean-DASH hybrid)',
  'Ovo vegetarian',
  'Paleo',
  'Pegan (Paleo + Vegan hybrid)',
  'Pescatarian',
  'Plant-based',
  'Vegan',
  'Vegetarian',
  'Whole food plant-based',
].map(option => ({ value: option, label: option }));

const religiousRestrictionOptions = [
  'Buddhist',
  'Halal',
  'Jain',
  'Kosher',
  'Rastafarian Ital',
  'Seventh-day Adventist',
].map(option => ({ value: option, label: option }));

const allergyOptions = [
  'Alcohol intolerance',
  'Artificial sweetener sensitivity',
  'Caffeine sensitivity',
  'Celery allergy',
  'Egg allergy',
  'Fish allergy',
  'Food additive intolerance (e.g. preservatives, colourings)',
  'Fructose intolerance (hereditary or malabsorption)',
  'Gluten intolerance / Non-celiac gluten sensitivity',
  'Histamine intolerance',
  'Lactose intolerance',
  'Lupin allergy',
  'Milk allergy (cow’s milk protein: casein and/or whey)',
  'MSG sensitivity',
  'Mustard allergy',
  'Peanut allergy',
  'Salicylate sensitivity',
  'Sesame allergy',
  'Shellfish allergy',
  'Soy allergy',
  'Sulphite sensitivity',
  'Tree nut allergy (e.g. almond, cashew, walnut)',
  'Wheat allergy',
].map(option => ({ value: option, label: option }));

const cuisineOptions = [
  { value: 'African', label: 'African' },
  { value: 'American', label: 'American' },
  { value: 'Continental', label: 'Australian' },  // No actual australian cuisine. App returns Continental instead
  { value: 'British', label: 'British' },
  { value: 'Caribbean', label: 'Caribbean' },
  { value: 'Chinese', label: 'Chinese' },
  { value: 'Eastern European', label: 'Eastern European' },
  { value: 'French', label: 'French' },
  { value: 'German', label: 'German' },
  { value: 'Greek', label: 'Greek' },
  { value: 'Indian', label: 'Indian' },
  { value: 'Indonesian', label: 'Indonesian' },
  { value: 'Japanese', label: 'Japanese' },
  { value: 'Korean', label: 'Korean' },
  { value: 'Latin American', label: 'Latin American' },
  { value: 'Malaysian', label: 'Malaysian' },
  { value: 'Mediterranean', label: 'Mediterranean' },
  { value: 'Mexican', label: 'Mexican' },
  { value: 'Middle Eastern', label: 'Middle Eastern' },
  { value: 'Nordic/Scandinavian', label: 'Nordic/Scandinavian' },
  { value: 'Singaporean', label: 'Singaporean' },
  { value: 'Spanish', label: 'Spanish' },
  { value: 'Thai', label: 'Thai' },
  { value: 'Vietnamese', label: 'Vietnamese' },
];

const healthConditionOptions = [
  { value: 'acne', label: 'Acne' },
  { value: 'adhd', label: 'ADHD' },
  { value: 'adrenal_fatigue', label: 'Adrenal Fatigue' },
  { value: 'alzheimers', label: "Alzheimer's Disease" },
  { value: 'anaemia', label: 'Anaemia' },
  { value: 'anticoagulant_therapy', label: 'Anticoagulant Therapy' },
  { value: 'anxiety', label: 'Anxiety' },
  { value: 'arthritis', label: 'Arthritis' },
  { value: 'autism_spectrum_disorder', label: 'Autism Spectrum Disorder' },
  { value: 'bile_acid_malabsorption', label: 'Bile Acid Malabsorption' },
  { value: 'breastfeeding', label: 'Breastfeeding' },
  { value: 'cancer_related_weight_loss', label: 'Cancer Related Weight Loss / Cachexia' },
  { value: 'cardiovascular_disease', label: 'Cardiovascular Disease' },
  { value: 'chronic_hives', label: 'Chronic Hives / Urticaria' },
  { value: 'chronic_kidney_disease', label: 'Chronic Kidney Disease (CKD)' },
  { value: 'coeliac_disease', label: 'Coeliac Disease' },
  { value: 'congestive_heart_failure', label: 'Congestive Heart Failure' },
  { value: 'cushings_syndrome', label: "Cushing's Syndrome" },
  { value: 'dementia', label: 'Dementia' },
  { value: 'depression_anxiety', label: 'Depression / Anxiety' },
  { value: 'disordered_eating', label: 'Disordered Eating' },
  { value: 'diverticulosis', label: 'Diverticulosis / Diverticulitis' },
  { value: 'eczema', label: 'Eczema' },
  { value: 'ed_recovery', label: 'ED Recovery' },
  { value: 'endometriosis', label: 'Endometriosis' },
  { value: 'epilepsy', label: 'Epilepsy' },
  { value: 'erectile_dysfunction', label: 'Erectile Dysfunction' },
  { value: 'female_fertility', label: 'Female Fertility' },
  { value: 'gallbladder_disease', label: 'Gallbladder Disease' },
  { value: 'gastroparesis', label: 'Gastroparesis' },
  { value: 'gerd', label: 'GERD / Acid Reflux' },
  { value: 'gestational_diabetes', label: 'Gestational Diabetes' },
  { value: 'gout', label: 'Gout' },
  { value: 'hemochromatosis', label: 'Hemochromatosis' },
  { value: 'high_cholesterol', label: 'High Cholesterol / Dyslipidemia' },
  { value: 'hypertension', label: 'Hypertension' },
  { value: 'hyperthyroidism', label: "Hyperthyroidism / Graves' Disease" },
  { value: 'hypothyroidism', label: "Hypothyroidism / Hashimoto's Disease" },
  { value: 'ibd', label: "IBD (Crohn's Disease, Ulcerative Colitis)" },
  { value: 'ibs', label: 'IBS (Irritable Bowel Syndrome)' },
  { value: 'kidney_stones', label: 'Kidney Stones' },
  { value: 'liver_cirrhosis', label: 'Liver Cirrhosis' },
  { value: 'liver_disease', label: 'Liver Disease / Fatty Liver' },
  { value: 'low_residue_cancer_diet', label: 'Low-Residue Cancer Diet' },
  { value: 'lupus', label: 'Lupus' },
  { value: 'male_fertility', label: 'Male Fertility' },
  { value: 'malnutrition', label: 'Malnutrition' },
  { value: 'mcas', label: 'Mast Cell Activation Syndrome (MCAS)' },
  { value: 'menopause', label: 'Menopause' },
  { value: 'metabolic_syndrome', label: 'Metabolic Syndrome' },
  { value: 'migraine', label: 'Migraine / Vestibular Migraine' },
  { value: 'multiple_sclerosis', label: 'Multiple Sclerosis' },
  { value: 'muscle_gain', label: 'Muscle Gain' },
  { value: 'muscle_loss', label: 'Muscle Loss' },
  { value: 'nephrotic_syndrome', label: 'Nephrotic Syndrome' },
  { value: 'neutropenic_diet', label: 'Neutropenic Diet' },
  { value: 'osteoarthritis', label: 'Osteoarthritis' },
  { value: 'osteopenia', label: 'Osteopenia' },
  { value: 'osteoporosis', label: 'Osteoporosis' },
  { value: 'overweight_obesity', label: 'Overweight / Obesity' },
  { value: 'pancreatitis', label: 'Pancreatitis' },
  { value: 'parkinsons_disease', label: "Parkinson's Disease" },
  { value: 'pcos', label: 'PCOS' },
  { value: 'peptic_ulcers', label: 'Peptic Ulcers' },
  { value: 'pmdd', label: 'PMDD' },
  { value: 'pms', label: 'PMS' },
  { value: 'post_bariatric_surgery', label: 'Post-Bariatric Surgery' },
  { value: 'pre_bariatric_surgery', label: 'Pre-Bariatric Surgery' },
  { value: 'prediabetes', label: 'Prediabetes / Insulin Resistance' },
  { value: 'pregnancy_trimester_one', label: 'Pregnancy (Trimester One)' },
  { value: 'pregnancy_trimester_two', label: 'Pregnancy (Trimester Two)' },
  { value: 'pregnancy_trimester_three', label: 'Pregnancy (Trimester Three)' },
  { value: 'psoriasis', label: 'Psoriasis' },
  { value: 'rheumatoid_arthritis', label: 'Rheumatoid Arthritis' },
  { value: 'rosacea', label: 'Rosacea' },
  { value: 'sarcopenia', label: 'Sarcopenia' },
  { value: 'sibo', label: 'SIBO (Small Intestinal Bacterial Overgrowth)' },
  { value: 'stroke_prevention', label: 'Stroke Prevention' },
  { value: 'type_1_diabetes', label: 'Type 1 Diabetes' },
  { value: 'type_2_diabetes', label: 'Type 2 Diabetes' },
  { value: 'underweight', label: 'Underweight' },
];

const budgetOptions: ButtonGroupOption[] = [
  {
    value: 5,
    label: 'Low',
    description: '$2 - $5'
  },
  {
    value: 8,
    label: 'Moderate',
    description: '$5 - $8'
  },
  {
    value: 12,
    label: 'High',
    description: '$8 - $12'
  },
  {
    value: 20,
    label: 'Premium',
    description: '$12+'
  }
];

const cookingTimeOptions: ButtonGroupOption[] = [
  {
    value: 15,
    label: 'Quick',
    description: '<15 mins'
  },
  {
    value: 30,
    label: 'Standard',
    description: '15-30 mins'
  },
  {
    value: 45,
    label: 'Moderate',
    description: '30-45 mins'
  },
  {
    value: 60,
    label: 'Extended',
    description: '45-60 mins'
  },
  {
    value: 120,
    label: 'Leisurely',
    description: '>60 mins'
  }
];

/**
 * Main Create Meal Plan Form Component
 */
export const CreateMealPlanForm: React.FC<CreateMealPlanFormProps> = ({
  onSubmit,
  isSubmitting = false,
  error = null,
  onClearError
}) => {
  const logger = getLoggerWithPrefix('CreateMealPlanForm');
  const navigate = useNavigate();
  const { isDark } = useTheme();

  const [formData, setFormData] = useState<CreateMealPlanRequestFrontend>({
    mealCount: undefined,
    mealTypes: [],
    dietaryRestrictions: [],
    religiousRestrictions: [],
    allergies: [],
    dislikedIngredients: [],
    cuisinePreferences: [],
    budgetAud: undefined,
    servingsPerMeal: undefined,
    label: '',
    maxPrepTime: undefined,
    userGuidance: '',
  });

  // Field refs for scroll-to-error functionality
  const fieldRefs = useRef<Record<string, HTMLElement | null>>({});

  // Field errors state
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  // Validation function
  const validateField = (fieldName: string, value: unknown): string => {
    const maxDays = 7;
    switch (fieldName) {
      case 'maxPrepTime':
        if (value === undefined || value === null) {
          return 'Available cooking time is required';
        }
        return '';

      case 'budgetAud':
        if (value === undefined || value === null) {
          return 'Budget per meal is required';
        } else if (typeof value === 'number' && value <= 0) {
          return 'Budget per meal must be greater than 0';
        }
        return '';

      case 'servingsPerMeal':
        if (value === undefined || value === null) {
          return 'Servings per meal is required';
        } else if (typeof value === 'number' && value <= 0) {
          return 'Servings per meal must be greater than 0';
        }
        return '';

      case 'mealTypes':
        if (!value || (Array.isArray(value) && value.length === 0)) {
          return 'At least one meal type must be selected';
        }
        return '';

      case 'mealCount':
        if (value === undefined || value === null || (typeof value === 'number' && (value < 1 || value > maxDays))) {
          return `Number of days must be between 1 and ${maxDays}`;
        }
        return '';
      case 'healthConditions':
        if (Array.isArray(value) && value.length > 0 && value.length > 4) {
          return 'You can select up to 4 health conditions only';
        }
        return '';

      default:
        return '';
    }
  };

  // Handle field changes with validation
  const handleFieldChange = (fieldName: string, value: unknown) => {
    setFormData(prev => ({ ...prev, [fieldName]: value }));

    // Clear field error when user starts typing
    if (fieldErrors[fieldName]) {
      setFieldErrors(prev => ({ ...prev, [fieldName]: '' }));
    }
  };


  // Scroll to first error field
  const scrollToFirstError = (errors: Record<string, string>) => {
    const firstErrorField = Object.keys(errors)[0];
    if (firstErrorField && fieldRefs.current[firstErrorField]) {
      fieldRefs.current[firstErrorField]?.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
      fieldRefs.current[firstErrorField]?.focus();
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Clear any existing errors
    if (onClearError) {
      onClearError();
    }

    // Validate all required fields
    const newErrors: Record<string, string> = {};
    const requiredFields = ['maxPrepTime', 'budgetAud', 'servingsPerMeal', 'mealTypes', 'mealCount', 'healthConditions'];

    requiredFields.forEach(fieldName => {
      const error = validateField(fieldName, formData[fieldName as keyof CreateMealPlanRequestFrontend]);
      if (error) {
        newErrors[fieldName] = error;
      }
    });

    // Validate health conditions (optional field, but validate if not empty)
    if (formData.healthConditions && formData.healthConditions.length > 0) {
      const healthConditionsError = validateField('healthConditions', formData.healthConditions);
      if (healthConditionsError) {
        newErrors.healthConditions = healthConditionsError;
      }
    }

    // Update field errors
    setFieldErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      scrollToFirstError(newErrors);
      return;
    }

    // Use parent's submit handler if provided, otherwise fallback to default behavior
    if (onSubmit) {
      await onSubmit(formData);
    } else {
      // Fallback behavior for backward compatibility
      logger.log('Creating meal plan with fallback handler', {
        mealTypes: formData.mealTypes,
        mealCount: formData.mealCount,
      });
      navigate('/meal-plans');
    }
  };

  const handleMealTypeChange = (selectedValues: string[]) => {
    handleFieldChange('mealTypes', selectedValues as MealType[]);
  };

  const handleArrayFieldChange = (
    field: keyof CreateMealPlanRequestFrontend,
    selectedValues: string[]
  ) => {
    handleFieldChange(field, selectedValues);
  };

  // Helper function to convert comma-delimited text to array
  const handleDislikedIngredientsChange = (value: string) => {
    const ingredients = value
      .split(',')
      .map(item => item.trim())
      .filter(item => item.length > 0);
    handleFieldChange('dislikedIngredients', ingredients);
  };

  // Calculate estimated generation time
  const calculateEstimatedTime = (): string => {
    const mealCount = formData.mealCount;
    const mealTypesCount = formData.mealTypes?.length || 0;

    if (!mealCount || mealCount <= 0 || mealTypesCount <= 0) {
      return 'Unknown';
    }

    const estimatedSeconds = calculateEstimatedTimeSeconds(mealCount, mealTypesCount);
    return formatEstimatedTime(estimatedSeconds);
  };

  return (
    <Card className="p-6">
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Error Display */}
        {error && (
          <div className={cn(
            'p-4 rounded-md border',
            isDark
              ? 'bg-error-900/20 border-error-700 text-error-200'
              : 'bg-error-50 border-error-200 text-error-700'
          )}>
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium">{error}</p>
              </div>
              {onClearError && (
                <div className="ml-auto pl-3">
                  <button
                    type="button"
                    onClick={onClearError}
                    className={cn(
                      'inline-flex rounded-md p-1.5 focus:outline-none focus:ring-2 focus:ring-offset-2',
                      isDark
                        ? 'text-error-200 hover:bg-error-800 focus:ring-error-600'
                        : 'text-error-400 hover:bg-error-100 focus:ring-error-500'
                    )}
                  >
                    <span className="sr-only">Dismiss</span>
                    <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Label */}
        <div ref={(el) => { fieldRefs.current.label = el; }}>
          <FormField
            label="Meal Plan Label"
          >
            <Input
              value={formData.label || ''}
              onChange={(e) => handleFieldChange('label', e.target.value)}
              placeholder="e.g., Mediterranean Wellness Plan"
            />
          </FormField>
        </div>

        {/* Meal Count */}
        <div ref={(el) => { fieldRefs.current.mealCount = el; }}>
          <FormField
            label="Number of days"
            required
            error={fieldErrors.mealCount}
          >
            <NumberField
              label=""
              placeholder="Enter number of days"
              value={formData.mealCount ?? ''}
              onChange={(value: string) => {
                handleFieldChange('mealCount', parseInt(value) || 0);
              }}
              min="1"
              max="7"
            />
          </FormField>
        </div>

        {/* Meal Types */}
        <div ref={(el) => { fieldRefs.current.mealTypes = el; }}>
          <FormField
            label="Meal Types"
            required
            error={fieldErrors.mealTypes}
          >
            <MultiSelectInput
              options={mealTypeOptions}
              value={formData.mealTypes}
              onChange={(selectedValues) => {
                handleMealTypeChange(selectedValues);
              }}
              placeholder="Select meal types..."
              dialogTitle="Select Meal Types"
              searchPlaceholder="Search meal types..."
              searchable={false}
            />
          </FormField>
        </div>

        {/* Available Cooking Time */}
        <div ref={(el) => { fieldRefs.current.maxPrepTime = el; }}>
          <FormField
            label="Available cooking time"
            required
            error={fieldErrors.maxPrepTime}
          >
            <ButtonGroup
              options={cookingTimeOptions}
              value={formData.maxPrepTime}
              onChange={(value: string | number) => {
                handleFieldChange('maxPrepTime', typeof value === 'string' ? parseFloat(value) : value);
              }}
              orientation="horizontal"
              maxColumns={5}
            />
          </FormField>
        </div>

        {/* Budget */}
        <div ref={(el) => { fieldRefs.current.budgetAud = el; }}>
          <FormField
            label="Budget per meal (AUD)"
            required
            error={fieldErrors.budgetAud}
          >
            <ButtonGroup
              options={budgetOptions}
              value={formData.budgetAud}
              onChange={(value: string | number) => {
                handleFieldChange('budgetAud', typeof value === 'string' ? parseFloat(value) : value);
              }}
              orientation="horizontal"
            />
          </FormField>
        </div>

        {/* Servings Per Meal */}
        <div ref={(el) => { fieldRefs.current.servingsPerMeal = el; }}>
          <FormField
            label="Servings Per Meal"
            required
            error={fieldErrors.servingsPerMeal}
          >
            <NumberField
              label=""
              value={formData.servingsPerMeal || ''}
              onChange={(value: string) => {
                handleFieldChange('servingsPerMeal', value ? parseInt(value) : undefined);
              }}
              placeholder="Enter number of servings per meal"
              min="1"
              max="10"
            />
          </FormField>
        </div>

        {/* Dietary Restrictions */}
        <FormField
          label="Dietary Restrictions"
        >
          <MultiSelectInput
            options={dietaryRestrictionOptions}
            value={formData.dietaryRestrictions || []}
            onChange={(selectedValues) => handleArrayFieldChange('dietaryRestrictions', selectedValues)}
            placeholder="Select dietary restrictions..."
            dialogTitle="Select Dietary Restrictions"
            searchPlaceholder="Search dietary restrictions..."
            searchable={true}
          />
        </FormField>

        {/* Religious Restrictions */}
        <FormField
          label="Religious Restrictions"
        >
          <MultiSelectInput
            options={religiousRestrictionOptions}
            value={formData.religiousRestrictions || []}
            onChange={(selectedValues) => handleArrayFieldChange('religiousRestrictions', selectedValues)}
            placeholder="Select religious restrictions..."
            dialogTitle="Select Religious Restrictions"
            searchPlaceholder="Search religious restrictions..."
            searchable={true}
          />
        </FormField>

        {/* Allergies */}
        <FormField
          label="Allergies"
        >
          <MultiSelectInput
            options={allergyOptions}
            value={formData.allergies || []}
            onChange={(selectedValues) => handleArrayFieldChange('allergies', selectedValues)}
            placeholder="Select allergies..."
            dialogTitle="Select Allergies"
            searchPlaceholder="Search allergies..."
            searchable={true}
          />
        </FormField>

        {/* Disliked Ingredients */}
        <FormField
          label="Disliked Ingredients"
        >
          <div className="space-y-2">
            <TextArea
              rows={3}
              onChange={(e) => handleDislikedIngredientsChange(e.target.value)}
              placeholder="Enter ingredients separated by commas (e.g., mushrooms, olives, coriander)"
              disabled={ false }
            />
          </div>
        </FormField>

        {/* Cuisine Preferences */}
        <FormField
          label="Cuisine Preferences"
        >
          <MultiSelectInput
            options={cuisineOptions}
            value={formData.cuisinePreferences || []}
            onChange={(selectedValues) => handleArrayFieldChange('cuisinePreferences', selectedValues)}
            placeholder="Select cuisine preferences..."
            dialogTitle="Select Cuisine Preferences"
            searchPlaceholder="Search cuisines..."
            searchable={true}
          />
        </FormField>

        {/* Health Conditions */}
        <div ref={(el) => { fieldRefs.current.healthConditions = el; }}>
          <FormField
            label="Health Conditions (up to 4)"
            error={fieldErrors.healthConditions}
          >
          <MultiSelectInput
            options={healthConditionOptions}
            value={formData.healthConditions || []}
            onChange={(selectedValues) => {
              handleArrayFieldChange('healthConditions', selectedValues);
            }}
            placeholder="Select health conditions..."
            dialogTitle="Select Health Conditions"
            searchPlaceholder="Search health conditions..."
            searchable={true}
          />
          </FormField>
        </div>

        {/* User Guidance */}
        <FormField
          label="User Guidance"
        >
          <div className="space-y-2">
            <TextArea
              rows={4}
              value={formData.userGuidance || ''}
              onChange={(e) => handleFieldChange('userGuidance', e.target.value)}
              placeholder="e.g., Focus on Mediterranean-style meals with lots of fresh vegetables, or prefer quick weeknight dinners"
              disabled={false}
              maxLength={140}
            />
            <p className={cn(
              'text-xs',
              isDark ? 'text-secondary-400' : 'text-secondary-500'
            )}>
              Provide free-form guidance to customize meal generation (max 140 characters)
            </p>
          </div>
        </FormField>

        {/* Estimated Generation Time */}
        <div className={cn(
          'text-sm text-right',
          isDark ? 'text-secondary-300' : 'text-secondary-600'
        )}>
          Estimated generation time: {calculateEstimatedTime()}
        </div>

        {/* Submit Button */}
        <div className="pt-4 flex gap-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate(-1)}
            className="flex-1"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={isSubmitting}
            className="flex-1"
          >
            {isSubmitting ? (
              <div className="flex items-center justify-center space-x-2">
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                <span>Creating Meal Plan...</span>
              </div>
            ) : (
              'Create Meal Plan'
            )}
          </Button>
        </div>
      </form>
    </Card>
  );
};

export default CreateMealPlanForm;